import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { Checkbox, Form, Input, Select } from 'antd'
import type { FormInstance } from 'antd/es/form'
import { formatPhone, normalizePhone } from '@/lib/phone'
import { entityNameSchema, requiredEmailSchema, shortTextSchema, zodAntdValidator } from '@/lib/validation'
import {
  baseClientTypeOptions,
  normalizeWorkspaceValue,
  type ClientFormValues,
} from '../../clientFormShared'
import type { PbiGroupOption } from '../../types'
import { ClientThemeColorFields } from '../../components/ClientThemeColorFields'

const clientNameSchema = entityNameSchema('Client name')
const principalContactSchema = shortTextSchema('Principal contact')
const emailSchema = requiredEmailSchema('Email')
const addressSchema = shortTextSchema('Current Address', { required: false })
const streetSchema = shortTextSchema('Street', { required: false })
const suburbSchema = shortTextSchema('Suburb/City', { required: false })
const stateSchema = shortTextSchema('State', { required: false })
const postcodeSchema = shortTextSchema('Postcode', { required: false })

function useWorkspaceOptions(form: FormInstance<ClientFormValues>, pbiGroups: PbiGroupOption[]) {
  const workspaceField = Form.useWatch('powerBIWorkspace', form)
  const workspaceNameField = Form.useWatch('powerBIWorkspaceName', form)

  return useMemo(() => {
    const options = pbiGroups.map((group) => ({ value: String(group.value), label: group.name }))
    const workspaceValue = normalizeWorkspaceValue(workspaceField)
    const workspaceName = typeof workspaceNameField === 'string' ? workspaceNameField : ''

    if (workspaceValue && !options.some((option) => option.value === workspaceValue)) {
      options.push({
        value: workspaceValue,
        label: workspaceName || workspaceValue,
      })
    }

    return options
  }, [pbiGroups, workspaceField, workspaceNameField])
}

function useClientEditTypeOptions(isAdmin: boolean) {
  return useMemo(
    () => (isAdmin ? [...baseClientTypeOptions, { label: 'Advisor', value: 3 as const }] : baseClientTypeOptions),
    [isAdmin]
  )
}

type ClientDetailsFormFieldsProps = {
  form: FormInstance<ClientFormValues>
  pbiGroups: PbiGroupOption[]
  pbiGroupsLoading: boolean
  logoSection: ReactNode
  isAdmin: boolean
}

export function ClientDetailsFormFields({
  form,
  pbiGroups,
  pbiGroupsLoading,
  logoSection,
  isAdmin,
}: ClientDetailsFormFieldsProps) {
  const workspaceOptions = useWorkspaceOptions(form, pbiGroups)
  const typeOptions = useClientEditTypeOptions(isAdmin)

  return (
    <>
      <Form.Item name="statusId" style={{ display: 'none' }}>
        <Input type="hidden" />
      </Form.Item>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Form.Item name="name" label="Client Name" rules={[{ validator: zodAntdValidator(clientNameSchema) }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="powerBIWorkspace"
          label="Workspace ID"
          rules={[{ required: true, message: 'Workspace ID is required' }]}
        >
          <Select
            loading={pbiGroupsLoading}
            options={workspaceOptions}
            showSearch
            optionFilterProp="label"
            onChange={(value, option) => {
              const nextValue = Array.isArray(value) ? value[0] : value
              const nextOption = Array.isArray(option) ? option[0] : option
              form.setFieldValue('powerBIWorkspace', normalizeWorkspaceValue(nextValue))
              form.setFieldValue('powerBIWorkspaceName', typeof nextOption?.label === 'string' ? nextOption.label : '')
            }}
          />
        </Form.Item>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Form.Item
          name="principalContact"
          label="Principal Contact"
          rules={[{ validator: zodAntdValidator(principalContactSchema) }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="email" label="Email" rules={[{ validator: zodAntdValidator(emailSchema) }]}>
          <Input />
        </Form.Item>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Form.Item
          name="phone"
          label="Phone"
          rules={[
            { required: true, message: 'Phone is required' },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve()
                return normalizePhone(value).length === 10
                  ? Promise.resolve()
                  : Promise.reject(new Error('Phone number must be exactly 10 digits.'))
              },
            },
          ]}
          getValueFromEvent={(event) => normalizePhone(event?.target?.value).slice(0, 10)}
          getValueProps={(value) => ({ value: formatPhone(value) === '—' ? '' : formatPhone(value) })}
        >
          <Input />
        </Form.Item>
        <Form.Item name="typeId" label="Client Type">
          <Select options={typeOptions} />
        </Form.Item>
      </div>

      {logoSection}

      <ClientThemeColorFields />

      {isAdmin && (
        <Form.Item name="showProjectStatus" valuePropName="checked">
          <Checkbox>Show Project Status Menu</Checkbox>
        </Form.Item>
      )}
      {isAdmin && (
        <Form.Item name="showAdvisoryMapping" valuePropName="checked">
          <Checkbox>Show Advisory Mapping Menu</Checkbox>
        </Form.Item>
      )}
      <Form.Item name="address" label="Current Address" rules={[{ validator: zodAntdValidator(addressSchema) }]}>
        <Input />
      </Form.Item>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Form.Item name="street" label="Street" rules={[{ validator: zodAntdValidator(streetSchema) }]}>
          <Input />
        </Form.Item>
        <Form.Item name="suburb" label="Suburb/City" rules={[{ validator: zodAntdValidator(suburbSchema) }]}>
          <Input />
        </Form.Item>
        <Form.Item name="state" label="State" rules={[{ validator: zodAntdValidator(stateSchema) }]}>
          <Input />
        </Form.Item>
        <Form.Item name="postcode" label="Postcode" rules={[{ validator: zodAntdValidator(postcodeSchema) }]}>
          <Input />
        </Form.Item>
      </div>
    </>
  )
}
